import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { platformService } from '../services/api';
import { CreditPackage } from '../types';
import './PlatformSettings.css';

const emptyPackageForm = {
  name: '',
  description: '',
  emailCredits: '100',
  whatsappCredits: '100',
  priceInr: '499',
  sortOrder: '0',
};

const formatInr = (value: number) =>
  `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const PlatformSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingPageToggle, setSavingPageToggle] = useState(false);
  const [savingEmailToggle, setSavingEmailToggle] = useState(false);
  const [savingWhatsappToggle, setSavingWhatsappToggle] = useState(false);
  const [packageBusyId, setPackageBusyId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState({
    defaultEmailCredits: '100',
    defaultWhatsappCredits: '100',
    creditPackagesEnabled: false,
    creditsPageEnabled: true,
    emailCreditsVisible: true,
    whatsappCreditsVisible: true,
  });
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [packageForm, setPackageForm] = useState({ ...emptyPackageForm });
  const [editingPackageId, setEditingPackageId] = useState<number | null>(null);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [defaults, pkgList] = await Promise.all([
        platformService.getCreditDefaults(),
        platformService.listCreditPackages(),
      ]);
      setForm({
        defaultEmailCredits: String(defaults.defaultEmailCredits ?? 100),
        defaultWhatsappCredits: String(defaults.defaultWhatsappCredits ?? 100),
        creditPackagesEnabled: Boolean(defaults.creditPackagesEnabled),
        creditsPageEnabled: defaults.creditsPageEnabled !== false,
        emailCreditsVisible: defaults.emailCreditsVisible !== false,
        whatsappCreditsVisible: defaults.whatsappCreditsVisible !== false,
      });
      setPackages(pkgList);
    } catch {
      toast.error('Could not load platform settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const saveDefaults = async () => {
    const email = Number(form.defaultEmailCredits);
    const whatsapp = Number(form.defaultWhatsappCredits);
    if (!Number.isFinite(email) || email < 0 || !Number.isFinite(whatsapp) || whatsapp < 0) {
      toast.error('Credits must be zero or a positive number');
      return;
    }
    setSavingDefaults(true);
    try {
      const defaults = await platformService.updateCreditDefaults({
        defaultEmailCredits: email,
        defaultWhatsappCredits: whatsapp,
      });
      setForm((p) => ({
        ...p,
        defaultEmailCredits: String(defaults.defaultEmailCredits),
        defaultWhatsappCredits: String(defaults.defaultWhatsappCredits),
        creditPackagesEnabled: defaults.creditPackagesEnabled,
        creditsPageEnabled: defaults.creditsPageEnabled !== false,
        emailCreditsVisible: defaults.emailCreditsVisible !== false,
        whatsappCreditsVisible: defaults.whatsappCreditsVisible !== false,
      }));
      toast.success('Default credits saved. New hostels will receive these amounts.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not save settings');
    } finally {
      setSavingDefaults(false);
    }
  };

  const toggleCreditsPage = async () => {
    const next = !form.creditsPageEnabled;
    setSavingPageToggle(true);
    try {
      const defaults = await platformService.updateCreditDefaults({
        creditsPageEnabled: next,
      });
      setForm((p) => ({ ...p, creditsPageEnabled: defaults.creditsPageEnabled !== false }));
      toast.success(
        defaults.creditsPageEnabled !== false
          ? 'Credits page is visible to hostel admins.'
          : 'Credits page is hidden from hostel admins.',
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not update feature toggle');
    } finally {
      setSavingPageToggle(false);
    }
  };

  const toggleEmailCreditsVisible = async () => {
    const next = !form.emailCreditsVisible;
    setSavingEmailToggle(true);
    try {
      const defaults = await platformService.updateCreditDefaults({
        emailCreditsVisible: next,
      });
      setForm((p) => ({ ...p, emailCreditsVisible: defaults.emailCreditsVisible !== false }));
      toast.success(
        defaults.emailCreditsVisible !== false
          ? 'Email credits are visible on the hostel Credits page.'
          : 'Email credits are hidden from hostel admins.',
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not update feature toggle');
    } finally {
      setSavingEmailToggle(false);
    }
  };

  const toggleWhatsappCreditsVisible = async () => {
    const next = !form.whatsappCreditsVisible;
    setSavingWhatsappToggle(true);
    try {
      const defaults = await platformService.updateCreditDefaults({
        whatsappCreditsVisible: next,
      });
      setForm((p) => ({ ...p, whatsappCreditsVisible: defaults.whatsappCreditsVisible !== false }));
      toast.success(
        defaults.whatsappCreditsVisible !== false
          ? 'WhatsApp credits are visible on the hostel Credits page.'
          : 'WhatsApp credits are hidden from hostel admins.',
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not update feature toggle');
    } finally {
      setSavingWhatsappToggle(false);
    }
  };

  const togglePackages = async () => {
    const next = !form.creditPackagesEnabled;
    setSavingToggle(true);
    try {
      const defaults = await platformService.updateCreditDefaults({
        creditPackagesEnabled: next,
      });
      setForm((p) => ({
        ...p,
        creditPackagesEnabled: defaults.creditPackagesEnabled,
        creditsPageEnabled: defaults.creditsPageEnabled !== false,
        emailCreditsVisible: defaults.emailCreditsVisible !== false,
        whatsappCreditsVisible: defaults.whatsappCreditsVisible !== false,
      }));
      toast.success(
        defaults.creditPackagesEnabled
          ? 'Hostels can now buy credit packages from the Credits page.'
          : 'Credit package purchases are hidden from hostels.',
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not update feature toggle');
    } finally {
      setSavingToggle(false);
    }
  };

  const resetPackageForm = () => {
    setPackageForm({ ...emptyPackageForm });
    setEditingPackageId(null);
  };

  const startEditPackage = (pkg: CreditPackage) => {
    setEditingPackageId(pkg.id);
    setPackageForm({
      name: pkg.name,
      description: pkg.description || '',
      emailCredits: String(pkg.emailCredits),
      whatsappCredits: String(pkg.whatsappCredits),
      priceInr: String(pkg.priceInr),
      sortOrder: String(pkg.sortOrder),
    });
  };

  const savePackage = async () => {
    const name = packageForm.name.trim();
    const emailCredits = Number(packageForm.emailCredits);
    const whatsappCredits = Number(packageForm.whatsappCredits);
    const priceInr = Number(packageForm.priceInr);
    const sortOrder = Number(packageForm.sortOrder);

    if (!name) {
      toast.error('Package name is required');
      return;
    }
    if (
      !Number.isFinite(emailCredits) ||
      emailCredits < 0 ||
      !Number.isFinite(whatsappCredits) ||
      whatsappCredits < 0 ||
      (emailCredits <= 0 && whatsappCredits <= 0)
    ) {
      toast.error('Add at least one email or WhatsApp credit');
      return;
    }
    if (!Number.isFinite(priceInr) || priceInr < 0) {
      toast.error('Price must be zero or a positive number');
      return;
    }

    const payload = {
      name,
      description: packageForm.description.trim() || null,
      emailCredits,
      whatsappCredits,
      priceInr,
      isActive: true,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
    };

    setPackageBusyId(editingPackageId ?? 'new');
    try {
      if (editingPackageId) {
        const updated = await platformService.updateCreditPackage(editingPackageId, payload);
        setPackages((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        toast.success('Package updated');
      } else {
        const created = await platformService.createCreditPackage(payload);
        setPackages((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id));
        toast.success('Package created');
      }
      resetPackageForm();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not save package');
    } finally {
      setPackageBusyId(null);
    }
  };

  const togglePackageActive = async (pkg: CreditPackage) => {
    setPackageBusyId(pkg.id);
    try {
      const updated = await platformService.updateCreditPackage(pkg.id, { isActive: !pkg.isActive });
      setPackages((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      toast.success(updated.isActive ? 'Package enabled' : 'Package disabled');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not update package');
    } finally {
      setPackageBusyId(null);
    }
  };

  const deletePackage = async (pkg: CreditPackage) => {
    if (!window.confirm(`Delete package "${pkg.name}"? This cannot be undone.`)) return;
    setPackageBusyId(pkg.id);
    try {
      await platformService.deleteCreditPackage(pkg.id);
      setPackages((prev) => prev.filter((p) => p.id !== pkg.id));
      if (editingPackageId === pkg.id) resetPackageForm();
      toast.success('Package deleted');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not delete package');
    } finally {
      setPackageBusyId(null);
    }
  };

  return (
    <div className="platform-settings-page">
      <header className="platform-settings-page__head">
        <h1>Settings</h1>
        <p>Platform messaging defaults, credit packages, and hostel self-service purchases.</p>
      </header>

      {loading ? (
        <p className="platform-settings-page__loading">Loading…</p>
      ) : (
        <>
          <section className="platform-settings-card">
            <h2 className="platform-settings-card__title">New hostel defaults</h2>
            <div className="platform-settings-card__grid">
              <label>
                Default email credits
                <input
                  type="number"
                  min={0}
                  value={form.defaultEmailCredits}
                  onChange={(e) => setForm((p) => ({ ...p, defaultEmailCredits: e.target.value }))}
                />
                <span className="platform-settings-hint">1 credit = 1 invoice email sent</span>
              </label>
              <label>
                Default WhatsApp credits
                <input
                  type="number"
                  min={0}
                  value={form.defaultWhatsappCredits}
                  onChange={(e) => setForm((p) => ({ ...p, defaultWhatsappCredits: e.target.value }))}
                />
                <span className="platform-settings-hint">1 credit = 1 SMS/WhatsApp reminder</span>
              </label>
            </div>
            <button
              type="button"
              className="platform-settings-save"
              onClick={() => void saveDefaults()}
              disabled={savingDefaults}
            >
              {savingDefaults ? 'Saving…' : 'Save defaults'}
            </button>
          </section>

          <section className="platform-settings-card">
            <div className="platform-settings-shop-head">
              <div>
                <h2 className="platform-settings-card__title">Hostel Credits page</h2>
                <p className="platform-settings-shop-desc">
                  Show or hide the Credits page in the hostel admin sidebar. Balances and purchases are only available
                  when this is on.
                </p>
              </div>
              <label className="platform-settings-toggle">
                <input
                  type="checkbox"
                  checked={form.creditsPageEnabled}
                  onChange={() => void toggleCreditsPage()}
                  disabled={savingPageToggle}
                />
                <span className="platform-settings-toggle__track" aria-hidden />
                <span className="platform-settings-toggle__label">
                  {form.creditsPageEnabled ? 'Page ON' : 'Page OFF'}
                </span>
              </label>
            </div>

            <div className="platform-settings-credit-toggles">
              <div className="platform-settings-shop-head platform-settings-shop-head--nested">
                <div>
                  <h3 className="platform-settings-card__subtitle">Email credits</h3>
                  <p className="platform-settings-shop-desc">
                    Show or hide the email credits balance card on the hostel Credits page.
                  </p>
                </div>
                <label className="platform-settings-toggle">
                  <input
                    type="checkbox"
                    checked={form.emailCreditsVisible}
                    onChange={() => void toggleEmailCreditsVisible()}
                    disabled={savingEmailToggle || !form.creditsPageEnabled}
                  />
                  <span className="platform-settings-toggle__track" aria-hidden />
                  <span className="platform-settings-toggle__label">
                    {form.emailCreditsVisible ? 'Visible' : 'Hidden'}
                  </span>
                </label>
              </div>

              <div className="platform-settings-shop-head platform-settings-shop-head--nested">
                <div>
                  <h3 className="platform-settings-card__subtitle">WhatsApp credits</h3>
                  <p className="platform-settings-shop-desc">
                    Show or hide the WhatsApp credits balance card on the hostel Credits page.
                  </p>
                </div>
                <label className="platform-settings-toggle">
                  <input
                    type="checkbox"
                    checked={form.whatsappCreditsVisible}
                    onChange={() => void toggleWhatsappCreditsVisible()}
                    disabled={savingWhatsappToggle || !form.creditsPageEnabled}
                  />
                  <span className="platform-settings-toggle__track" aria-hidden />
                  <span className="platform-settings-toggle__label">
                    {form.whatsappCreditsVisible ? 'Visible' : 'Hidden'}
                  </span>
                </label>
              </div>
            </div>
          </section>

          <section className="platform-settings-card platform-settings-card--shop">
            <div className="platform-settings-shop-head">
              <div>
                <h2 className="platform-settings-card__title">Credit packages for hostels</h2>
                <p className="platform-settings-shop-desc">
                  Define packs hostels can buy. When enabled, packages appear on the hostel admin Credits page.
                </p>
              </div>
              <label className="platform-settings-toggle">
                <input
                  type="checkbox"
                  checked={form.creditPackagesEnabled}
                  onChange={() => void togglePackages()}
                  disabled={savingToggle}
                />
                <span className="platform-settings-toggle__track" aria-hidden />
                <span className="platform-settings-toggle__label">
                  {form.creditPackagesEnabled ? 'Purchases ON' : 'Purchases OFF'}
                </span>
              </label>
            </div>

            <div className="platform-packages-table-wrap">
              <table className="platform-packages-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Email</th>
                    <th>WhatsApp</th>
                    <th>Price</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="platform-packages-empty">
                        No packages yet. Add one below.
                      </td>
                    </tr>
                  ) : (
                    packages.map((pkg) => (
                      <tr key={pkg.id} className={!pkg.isActive ? 'platform-packages-row--inactive' : ''}>
                        <td>
                          <strong>{pkg.name}</strong>
                          {pkg.description ? (
                            <span className="platform-packages-desc">{pkg.description}</span>
                          ) : null}
                        </td>
                        <td>{pkg.emailCredits.toLocaleString('en-IN')}</td>
                        <td>{pkg.whatsappCredits.toLocaleString('en-IN')}</td>
                        <td>{formatInr(pkg.priceInr)}</td>
                        <td>{pkg.sortOrder}</td>
                        <td>
                          <span className={`platform-packages-status${pkg.isActive ? ' is-active' : ''}`}>
                            {pkg.isActive ? 'Active' : 'Hidden'}
                          </span>
                        </td>
                        <td>
                          <div className="platform-packages-actions">
                            <button
                              type="button"
                              className="btn btn-xs btn-secondary"
                              onClick={() => startEditPackage(pkg)}
                              disabled={packageBusyId === pkg.id}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-xs btn-secondary"
                              onClick={() => void togglePackageActive(pkg)}
                              disabled={packageBusyId === pkg.id}
                            >
                              {pkg.isActive ? 'Hide' : 'Show'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-xs btn-danger"
                              onClick={() => void deletePackage(pkg)}
                              disabled={packageBusyId === pkg.id}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="platform-package-form">
              <h3>{editingPackageId ? 'Edit package' : 'Add package'}</h3>
              <div className="platform-package-form__grid">
                <label>
                  Name
                  <input
                    value={packageForm.name}
                    onChange={(e) => setPackageForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Starter"
                  />
                </label>
                <label>
                  Price (₹)
                  <input
                    type="number"
                    min={0}
                    value={packageForm.priceInr}
                    onChange={(e) => setPackageForm((p) => ({ ...p, priceInr: e.target.value }))}
                  />
                </label>
                <label>
                  Email credits
                  <input
                    type="number"
                    min={0}
                    value={packageForm.emailCredits}
                    onChange={(e) => setPackageForm((p) => ({ ...p, emailCredits: e.target.value }))}
                  />
                </label>
                <label>
                  WhatsApp credits
                  <input
                    type="number"
                    min={0}
                    value={packageForm.whatsappCredits}
                    onChange={(e) => setPackageForm((p) => ({ ...p, whatsappCredits: e.target.value }))}
                  />
                </label>
                <label className="platform-package-form__wide">
                  Description
                  <input
                    value={packageForm.description}
                    onChange={(e) => setPackageForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Short note for hostel admins"
                  />
                </label>
                <label>
                  Sort order
                  <input
                    type="number"
                    min={0}
                    value={packageForm.sortOrder}
                    onChange={(e) => setPackageForm((p) => ({ ...p, sortOrder: e.target.value }))}
                  />
                </label>
              </div>
              <div className="platform-package-form__actions">
                {editingPackageId ? (
                  <button type="button" className="btn btn-secondary" onClick={resetPackageForm}>
                    Cancel edit
                  </button>
                ) : null}
                <button
                  type="button"
                  className="platform-settings-save"
                  onClick={() => void savePackage()}
                  disabled={packageBusyId != null}
                >
                  {packageBusyId != null
                    ? 'Saving…'
                    : editingPackageId
                      ? 'Update package'
                      : 'Add package'}
                </button>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default PlatformSettings;
